import axios from "axios";
import { toast } from "react-toastify";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ADMINUSER,
  ERRORSTATUS,
  FAILEDSTATUS,
  IDLESTATUS,
  LOADINGSTATUS,
  secureSecretkey,
  SUCCESSSTATUS,
} from "../../assets/js/constants/constant";
import { replaceMultipleTokensFromAPI } from "../../assets/js/constants/commonFunctions";
import {
  getAllRestaurantOwnerUsersAPI,
  getAllRestaurantAPI,
  getAllMenuAPI,
  createRestaurantAPI,
  createUserAPI,
} from "../../assets/js/constants/APIURLs";

// 1. Get all users
export const getAllRestaurantOwnerUsers = createAsyncThunk(
  "admin/getAllRestaurantOwnerUsers",
  async (_, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getAllRestaurantOwnerUsersAPI),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message);
      return res.data.users;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

// Create Restaurant Owner/Admin User
export const createUser = createAsyncThunk(
  "admin/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(createUserAPI),
        {
          loggedInUserId,
          name: userData.name,
          phoneNumber: userData.phoneNumber,
          email: userData.email,
          password: userData.password,
          role: userData.role,
          subscribedPlan: userData.subscribedPlan,
          subscribedPlannedStartDate: userData.subscribedPlannedStartDate,
          subscribedPlannedEndDate: userData.subscribedPlannedEndDate,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      toast.success(res.data.message || "User created successfully");
      return res.data.user;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 3. Create restaurant
export const createRestaurant = createAsyncThunk(
  "restaurant/createRestaurant",
  async ({ restaurantId, restaurantData }, { rejectWithValue }) => {
    const loggedInUserId = localStorage.getItem("userId");
    try {
      const res = await axios.post(
        replaceMultipleTokensFromAPI(createRestaurantAPI),
        { loggedInUserId, restaurantId, restaurantData },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
            "Content-Type": "application/json",
          },
        },
      );
      const { restaurant, message } = res.data;
      toast.success(message || "Restaurant created successfully");
      return restaurant;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Something went wrong";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

// 4. Get all restaurants
export const getAllRestaurant = createAsyncThunk(
  "admin/getAllRestaurant",
  async (_, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getAllRestaurantAPI),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      const { data: restaurantData } = res.data;
      return restaurantData;
    } catch (err) {
      toast.error(err.response.data);
      return rejectWithValue(err.response.data);
    }
  },
);

// 5. Get all menus
export const getAllMenus = createAsyncThunk(
  "admin/getAllMenu",
  async (_, { rejectWithValue }) => {
    try {
      const loggedInUserId = localStorage.getItem("userId");
      const res = await axios.post(
        replaceMultipleTokensFromAPI(getAllMenuAPI),
        {
          loggedInUserId,
        },
        {
          headers: {
            Authorization: `Bearer ${secureSecretkey}`,
          },
        },
      );
      const { data: menuData } = res;
      return menuData;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  },
);

const adminSlice = createSlice({
  name: ADMINUSER,
  initialState: {
    users: [],
    status: IDLESTATUS,
    fetchUsersStatus: IDLESTATUS,
    createUserStatus: IDLESTATUS,
    updateUserStatus: IDLESTATUS,
    createRestaurantStatus: IDLESTATUS,
    restaurantsFetchStatus: IDLESTATUS,
    menuFetchStatus: IDLESTATUS,
    restaurants: [],
    menus: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // GET USERS
      .addCase(getAllRestaurantOwnerUsers.pending, (state) => {
        state.fetchUsersStatus = LOADINGSTATUS;
      })
      .addCase(getAllRestaurantOwnerUsers.fulfilled, (state, action) => {
        state.fetchUsersStatus = SUCCESSSTATUS;
        state.users = action.payload;
      })
      .addCase(getAllRestaurantOwnerUsers.rejected, (state, action) => {
        state.fetchUsersStatus = ERRORSTATUS;
        state.error = action.payload;
      })

      // CREATE USER
      .addCase(createUser.pending, (state) => {
        state.createUserStatus = LOADINGSTATUS;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createUserStatus = SUCCESSSTATUS;
        if (action.payload) {
          state.users.push(action.payload);
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createUserStatus = FAILEDSTATUS;
        state.error = action.payload;
      })

      .addCase(createRestaurant.pending, (state) => {
        state.loading = true;
        state.createRestaurantStatus = LOADINGSTATUS;
        state.error = null;
      })
      .addCase(createRestaurant.fulfilled, (state) => {
        state.createRestaurantStatus = SUCCESSSTATUS;
        state.loading = false;
      })
      .addCase(createRestaurant.rejected, (state, action) => {
        state.loading = false;
        state.createRestaurantStatus = ERRORSTATUS;
        state.error = action.payload;
      })

      // GET ALL RESTAURANT
      .addCase(getAllRestaurant.fulfilled, (state, action) => {
        state.restaurantsFetchStatus = SUCCESSSTATUS;
        state.restaurants = action.payload;
      })

      // GET ALL MENUS
      .addCase(getAllMenus.fulfilled, (state, action) => {
        state.menuFetchStatus = SUCCESSSTATUS;
        state.menus = action.payload;
      });
  },
});

export default adminSlice.reducer;
